import { RiDeleteBinFill, RiLoader3Line } from '@remixicon/react'
import { useMutation } from '@tanstack/react-query'
import { type PropsWithChildren, useState } from 'react'
import { toast } from 'sonner'

import { bundlesService } from '@/api/bundles/bundles-service'
import type { Bundle } from '@/api/bundles/bundles-types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'

interface DeleteBundleProps extends PropsWithChildren {
  bundle: Bundle
  open?: boolean
  disabled?: boolean
  onOpenChange?: (open: boolean) => void
}

export const DeleteBundle = ({
  bundle,
  open: externalOpen,
  disabled = false,
  onOpenChange: externalOnOpenChange,
  children
}: DeleteBundleProps) => {
  const [internalOpen, setInternalOpen] = useState(false)

  const open = externalOpen !== undefined ? externalOpen : internalOpen
  const setOpen = externalOnOpenChange ?? setInternalOpen

  const deleteBreakMutation = useMutation({
    mutationFn: () => bundlesService.delete(bundle.id),
    onSuccess: () => {
      setOpen(false)
      toast.success(`Bundle deleted successfully`)
    },
    meta: {
      // `skids`: for boxed products the backend creates or drops a Box unit with the bundle
      invalidatesQuery: ['orders', 'items', 'order', 'timeline-orders', 'skids']
    }
  })

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
    >
      <DialogTrigger asChild>
        {children ? (
          children
        ) : (
          <Button
            className='hover:[&_svg]:text-white-100 hover:bg-red-300'
            disabled={disabled || deleteBreakMutation.isPending}
            size='icon-sm'
            variant='ghost'
          >
            <RiDeleteBinFill />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure you want to delete bundle?</DialogTitle>
        </DialogHeader>
        <DialogFooter className='flex-row'>
          <DialogClose asChild>
            <Button
              className='w-20'
              variant='outline'
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            disabled={deleteBreakMutation.isPending}
            onClick={() => deleteBreakMutation.mutate()}
            className='w-20'
            variant='destructive'
          >
            {deleteBreakMutation.isPending ? <RiLoader3Line className='animate-spin' /> : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
